import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "https://bluewaterdubai.websiteseoonline.com";
const IMAGE_DELIVERY_BASE = "https://imagedelivery.net/WefzwPpyYAgz4nGrybaw0Q";
const FALLBACK_IMAGE = "/assets/img/popular/popular-1-1.jpg";
const DEFAULT_PAYLOAD = {
  offering_type: "",
  city: "",
  property_category: "",
  property_type: "",
  bedrooms: "",
  bathrooms: "",
  minPrice: "",
  maxPrice: "",
  minArea: "",
  maxArea: "",
  page: "1",
  pageSize: "8",
  sortBy: "id DESC",
};

function buildApiUrl(pathname) {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || API_BASE_URL).replace(/\/$/, "");
  return baseUrl + pathname;
}

function readStoredPayload() {
  try {
    const stored = window.sessionStorage.getItem("bluewaterPropertySearch");
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function paramValue(searchParams, key, fallback) {
  const value = searchParams.get(key);
  return value !== null ? value : fallback || "";
}

function payloadFromParams(searchParams) {
  const stored = readStoredPayload();

  return Object.keys(DEFAULT_PAYLOAD).reduce((payload, key) => {
    payload[key] = paramValue(searchParams, key, stored[key] || DEFAULT_PAYLOAD[key]);
    return payload;
  }, {});
}

function normalizeImagePath(image) {
  if (!image) {
    return FALLBACK_IMAGE;
  }

  if (/^https?:\/\//i.test(image) || image.startsWith("/")) {
    return image;
  }

  if (image.startsWith("assets/")) {
    return "/" + image;
  }

  return IMAGE_DELIVERY_BASE + "/" + image.replace(/^\/+/, "");
}

function formatPrice(price) {
  const numericPrice = Number(String(price || "").replace(/[^\d.]/g, ""));

  if (!numericPrice) {
    return "Price on request";
  }

  return "AED " + new Intl.NumberFormat("en-AE", { maximumFractionDigits: 0 }).format(numericPrice);
}

function formatLocation(property) {
  return [property.community, property.city].filter(Boolean).join(" - ") || "Dubai - UAE";
}

function offeringTypeBadge(property) {
  const rawType = String(property.offering_type || property.offeringType || "").trim();
  const normalizedType = rawType.toLowerCase();

  if (!rawType) {
    return null;
  }

  if (normalizedType.includes("rent") || ["rr", "cr", "r"].includes(normalizedType)) {
    return { label: "Rent", tone: "rent" };
  }

  if (normalizedType.includes("sale") || normalizedType.includes("buy") || ["rs", "cs", "s"].includes(normalizedType)) {
    return { label: "Sale", tone: "sale" };
  }

  return { label: rawType, tone: "default" };
}

function propertyUrl(property) {
  const referenceNumber = property.reference_number || property.id;
  return referenceNumber ? "/property-details/" + encodeURIComponent(referenceNumber) : "/property-details";
}

function absolutePropertyUrl(property) {
  const detailUrl = propertyUrl(property);

  if (/^https?:\/\//i.test(detailUrl) || typeof window === "undefined") {
    return detailUrl;
  }

  return window.location.origin + detailUrl;
}

function whatsappMessage(property) {
  const propertyTitle = property.title || "this property";
  const referenceNumber = property.reference_number || property.id;
  const messageParts = [
    "Hello Blue Water Real Estate, I am interested in " + propertyTitle + ".",
    referenceNumber ? "Reference: " + referenceNumber + "." : "",
    "Property details: " + absolutePropertyUrl(property),
    "Please share more information.",
  ];

  return messageParts.filter(Boolean).join(" ");
}

function whatsappUrl(property) {
  const rawPhone = property.agent_whatsapp || property.agent_phone || "";
  const phone = String(rawPhone).replace(/[^\d+]/g, "");
  const message = encodeURIComponent(whatsappMessage(property));

  if (!phone) {
    return "https://api.whatsapp.com/send?phone=971507455838&text=" + message;
  }

  return "https://api.whatsapp.com/send?phone=" + encodeURIComponent(phone) + "&text=" + message;
}

function phoneUrl(property) {
  const phone = String(property.agent_phone || "").replace(/[^\d+]/g, "");
  return phone ? "tel:" + phone : "tel:+97144514747";
}

function propertyImages(property) {
  if (!Array.isArray(property.photos) || !property.photos.length) {
    return [FALLBACK_IMAGE];
  }

  return property.photos.map((photo) => normalizeImagePath(photo.image)).filter(Boolean);
}

function openImageGallery(event, images) {
  event.preventDefault();

  const galleryItems = images.filter(Boolean).map((src) => ({ src }));
  const $ = window.jQuery || window.$;

  if (galleryItems.length && $?.magnificPopup?.open) {
    $.magnificPopup.open(
      {
        items: galleryItems,
        gallery: {
          enabled: true,
        },
        type: "image",
        mainClass: "mfp-zoom-in",
        removalDelay: 260,
      },
      0
    );
    return;
  }

  if (galleryItems[0]) {
    window.location.href = galleryItems[0].src;
  }
}

function searchSummary(payload) {
  return [
    payload.offering_type,
    payload.property_category,
    payload.property_type,
    payload.city,
    payload.bedrooms ? payload.bedrooms + " Bedrooms" : "",
    payload.bathrooms ? payload.bathrooms + " Bathrooms" : "",
  ].filter(Boolean);
}

function paginationItems(currentPage, totalPages) {
  const start = Math.max(currentPage - 2, 1);
  const end = Math.min(start + 4, totalPages);
  const adjustedStart = Math.max(end - 4, 1);
  const pages = [];

  for (let page = adjustedStart; page <= end; page += 1) {
    pages.push(page);
  }

  return pages;
}

function PropertyCard({ property }) {
  const images = propertyImages(property);
  const primaryImage = images[0] || FALLBACK_IMAGE;
  const badge = offeringTypeBadge(property);

  return (
    <div className="col-xxl-3 col-xl-3 col-lg-6 col-md-6">
      <div className="popular-list-1 property-listing-card">
        <div className="thumb-wrapper">
          <Link to={propertyUrl(property)} className="property-listing-image">
            <img src={primaryImage} alt={property.title || "Property"} />
          </Link>
          {badge ? <span className={`property-offering-badge ${badge.tone}`}>{badge.label}</span> : null}
          <div className="actions-style-2-wrapper">
            <div className="actions style-2">
              <Link to={propertyUrl(property)} className="icon-btn">
                <i className="fa-solid fa-link" />
              </Link>
              <button type="button" className="icon-btn property-gallery-button" onClick={(event) => openImageGallery(event, images)}>
                <i className="fa-solid fa-camera" />
              </button>
            </div>
          </div>
        </div>
        <div className="property-content">
          <div className="media-body">
            <h3 className="box-title">
              <Link to={propertyUrl(property)}>{property.title || "Property"}</Link>
            </h3>
            <div className="box-text">
              <div className="icon">
                <img src="/assets/img/icon/2.webp" alt="location" />
              </div>
              {formatLocation(property)}
            </div>
          </div>
          <ul className="property-featured">
            <li>
              <div className="icon">
                <img src="/assets/img/icon/bed.svg" alt="bed" />
              </div>
              Bed {property.bedrooms || "-"}
            </li>
            <li>
              <div className="icon">
                <img src="/assets/img/icon/bath.svg" alt="bath" />
              </div>
              Bath {property.No_of_Bathroom || "-"}
            </li>
            <li>
              <div className="icon">
                <img src="/assets/img/icon/sqft.svg" alt="area" />
              </div>
              {property.size || "-"} sqft
            </li>
          </ul>
          <div className="property-bottom">
            <h6 className="box-title">{formatPrice(property.price)}</h6>
            <a className="th-btn sm style3 pill" href={phoneUrl(property)} aria-label="Call agent">
              <i className="fa-solid fa-phone" />
            </a>
            <a className="th-btn sm style3 pill property-whatsapp-button" href={whatsappUrl(property)} target="_blank" rel="noreferrer" aria-label="WhatsApp agent">
              <img src="/assets/img/icon/3.webp" width="38" alt="WhatsApp" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function PropertySearchResult() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const payload = useMemo(() => payloadFromParams(searchParams), [searchParams]);
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const currentPage = Math.max(Number(payload.page) || 1, 1);
  const pageSize = Math.max(Number(payload.pageSize) || 8, 1);
  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  const summary = searchSummary(payload);

  useEffect(() => {
    const controller = new AbortController();

    async function loadSearchResults() {
      setStatus("loading");
      setError("");

      try {
        const response = await fetch(buildApiUrl("/api/propertysearch.ashx"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Unable to load search results");
        }

        const result = await response.json();

        if (result.result !== 1) {
          throw new Error(result.message || "Unable to load search results");
        }

        setProperties(Array.isArray(result.data) ? result.data : []);
        setTotal(Number(result.total) || 0);
        setStatus("ready");
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }

        setProperties([]);
        setTotal(0);
        setError(err.message || "Unable to load search results");
        setStatus("error");
      }
    }

    loadSearchResults();

    return () => controller.abort();
  }, [payload]);

  function goToPage(page) {
    const nextParams = new URLSearchParams();

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        nextParams.set(key, value);
      }
    });

    nextParams.set("page", String(page));
    navigate("/property-search-result?" + nextParams.toString());
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <>
      <div className="breadcumb-wrapper background-image" style={{ backgroundImage: "url(/assets/img/bg/breadcrumb-bg.jpg)" }}>
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">Property Search Result</h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>Property Search Result</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="popular-sec-1 space property-listing-page property-search-result-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8">
              <div className="title-area text-center">
                <h2 className="sec-title">Search Results</h2>
                {summary.length > 0 && <p className="property-search-summary">{summary.join(" / ")}</p>}
                {status === "ready" && <p className="property-search-total">{total} properties found</p>}
              </div>
            </div>
          </div>

          {status === "loading" && <div className="text-center">Loading search results...</div>}
          {status === "error" && <div className="text-center">{error}</div>}
          {status === "ready" && properties.length === 0 && <div className="text-center">No properties found.</div>}
          {status === "ready" && properties.length > 0 && (
            <>
              <div className="row gy-30 justify-content-center">
                {properties.map((property) => (
                  <PropertyCard property={property} key={property.id || property.reference_number || property.title} />
                ))}
              </div>
              {totalPages > 1 && (
                <div className="property-search-pagination">
                  <button type="button" className="th-btn sm style3 pill" onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1}>
                    <i className="far fa-arrow-left" />
                  </button>
                  {paginationItems(currentPage, totalPages).map((page) => (
                    <button type="button" className={"th-btn sm style3 pill" + (page === currentPage ? " active" : "")} onClick={() => goToPage(page)} key={page}>
                      {page}
                    </button>
                  ))}
                  <button type="button" className="th-btn sm style3 pill" onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages}>
                    <i className="far fa-arrow-right" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}

export default PropertySearchResult;
